import { AbstractGameView } from "@lib/titan2-slingo/dist/game/view/abstract/display/AbstractGameView";
import { EventObject } from "@lib/titan2/dist/util/event/EventObject";
import { RedFishKissType } from "../../../../../model/data/ChanceGamesData";
import { ParticleEmitter } from "@lib/titan2/dist/game/manager/data/ParticleEmitter";
import { LinkedListContainer } from "@pixi/particle-emitter";
import { TitanSprite } from "@lib/titan2/dist/game/control/TitanSprite";
import { OrientationType } from "@lib/titan2/dist/game/renderer/constant/RendererConstants";
import gsap from "gsap";
import { TitanSpine } from "@lib/titan2/dist/game/control/TitanSpine";
import { addToTitanSpineSlot, delayFor, getTitanSpineSlot, resetTitanSpine, SpineComplete, updateResponsiveSpineSlotsChildren } from "../../../../../utils/SpineUtils";
import { MouseUtil } from "@lib/titan2/dist/util/view/MouseUtil";
import { TitanText } from "@lib/titan2/dist/game/control/TitanText";
import { Service } from "@lib/titan2-slingo/dist/api/Service";
import { ALL_BOX_DIRECTIONS } from "../purplefish/FeaturePurpleFish";
import { Point } from "pixi.js";
import { ViewUtils } from "@lib/titan2/dist/util/view/ViewUtils";
import { SeededRandom } from "../../../../../utils/SeededRandom";
import { MouseBlocker } from "@lib/titan2/dist/game/control/MouseBlocker";
import { MouseBlockerProperties } from "@lib/titan2/dist/game/control/properties/MouseBlockerProperties";
import { GameLadyMeowSoundEvents } from "../../../../../controller/event/GameSountEvent";
export var FeatureRedFishState;
(function (FeatureRedFishState) {
    FeatureRedFishState[FeatureRedFishState["IDLE"] = 0] = "IDLE";
    FeatureRedFishState[FeatureRedFishState["MOVING_TO_OFFSCREEN"] = 1] = "MOVING_TO_OFFSCREEN";
    FeatureRedFishState[FeatureRedFishState["MOVING_TO_KISS_POSITION"] = 2] = "MOVING_TO_KISS_POSITION";
})(FeatureRedFishState || (FeatureRedFishState = {}));
export class FeatureRedFish extends AbstractGameView {
    constructor(factory, landscapeProperties, portraitProperties) {
        super(factory, landscapeProperties, portraitProperties);
        this.usedCrowdIndices = [];
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(FeatureRedFishEvents.FEATURE_PRESENT, this.featurePresent);
        this.registerGame(FeatureRedFishEvents.FEATURE_START, this.featureStart);
        this.registerGame(FeatureRedFishEvents.REVEAL_PRIZES, this.revealPrizes);
        this.registerGame(FeatureRedFishEvents.KISS, this.bid);
        this.registerGame(FeatureRedFishEvents.SOLD, this.sold);
        this.registerGame(FeatureRedFishEvents.CLEAR, this.clear);
    }
    create() {
        super.create();
        const backgroundBlocker = new MouseBlocker(new MouseBlockerProperties(0x642f0e, 1));
        this.addChild(backgroundBlocker);
        this.interactive = true;
        this.seededPrizePicker = new SeededRandom();
        this.seededPaddlePicker = new SeededRandom();
        TitanSprite.createSprite(this, "background", this.landscapeProperties.background, this.portraitProperties.background);
        this.cat = TitanSpine.createSpine(this, "cat", this.landscapeProperties.cat, this.portraitProperties.cat);
        this.cat.getSpine(OrientationType.LANDSCAPE).stateData.defaultMix = 0.2;
        this.cat.getSpine(OrientationType.PORTRAIT).stateData.defaultMix = 0.2;
        this.boxLeft = TitanSpine.createSpine(this, "boxLeft", this.landscapeProperties.boxLeft, this.portraitProperties.boxLeft);
        this.boxMiddle = TitanSpine.createSpine(this, "boxMiddle", this.landscapeProperties.boxMiddle, this.portraitProperties.boxMiddle);
        this.boxRight = TitanSpine.createSpine(this, "boxRight", this.landscapeProperties.boxRight, this.portraitProperties.boxRight);
        this.allBoxes = [this.boxLeft, this.boxMiddle, this.boxRight];
        this.boxesByDirection = { left: this.boxLeft, middle: this.boxMiddle, right: this.boxRight };
        this.allBoxes.forEach((box, index) => {
            MouseUtil.addMouseDown(box, () => {
                this.dispatchGame(new EventObject(FeatureRedFishEvents.PICK_SELECTED.getType(), { index }));
            }, null);
        });
        this.boxItems = this.landscapeProperties.boxItems.map((boxItem, index) => {
            const item = TitanSprite.createSprite(null, `box-item-${index}`, boxItem, this.portraitProperties.boxItems[index]);
            return item;
        });
        this.missedPrizeText0 = TitanText.createField(null, "picked-prize-text", this.landscapeProperties.pickedPrize, this.portraitProperties.pickedPrize);
        this.missedPrizeText1 = TitanText.createField(null, "picked-prize-text", this.landscapeProperties.pickedPrize, this.portraitProperties.pickedPrize);
        this.regularCrowdLayer = ViewUtils.createContainer(this, "regular-crowd-layer");
        this.crowd = this.landscapeProperties.crowd.map((crowdItem, index) => {
            const crowdCat = TitanSpine.createSpine(this.regularCrowdLayer, `crowd-item-${index}`, crowdItem, this.portraitProperties.crowd[index]);
            crowdCat.getSpine(OrientationType.LANDSCAPE).stateData.defaultMix = 0.2;
            crowdCat.getSpine(OrientationType.PORTRAIT).stateData.defaultMix = 0.2;
            crowdCat.play("idle", true, false, 0);
            const idleDuration = crowdCat.getTrackEntry(0).animation.duration;
            crowdCat.getTrackEntry(0).trackTime = (idleDuration / this.landscapeProperties.crowd.length) * index;
            return crowdCat;
        });
        this.siameseCrowdCat = TitanSpine.createSpine(this, "siameseCrowdCat", this.landscapeProperties.siameseCrowdCat, this.portraitProperties.siameseCrowdCat);
        this.siamesePrizeText = TitanText.createField(getTitanSpineSlot(this.siameseCrowdCat, "dynamic-value"), "prize-text", this.landscapeProperties.pickedPrize, this.portraitProperties.pickedPrize);
        this.paddleLayer = ViewUtils.createContainer(this, "paddle-layer");
        this.availableCrowdIndices = [];
        this.paddles = this.landscapeProperties.paddles.map((_, index) => {
            this.availableCrowdIndices.push(index);
            const paddle = TitanSpine.createSpine(this.paddleLayer, `paddle-${index}`, this.landscapeProperties.paddles[index], this.portraitProperties.paddles[index]);
            return paddle;
        });
        this.prizeTexts = this.paddles.map((paddle) => {
            return TitanText.createField(getTitanSpineSlot(paddle, "dynamic-value"), "prize-text", this.landscapeProperties.pickedPrize, this.portraitProperties.pickedPrize);
        });
        this.bidChangeText = TitanText.createField(this, this.landscapeProperties.bidChangeText, this.portraitProperties.bidChangeText);
        this.visible = false;
        this.titleText = TitanText.createField(this, "title-text", this.landscapeProperties.titleText, this.portraitProperties.titleText);
        this.helpText = TitanText.createField(this, "help-text", this.landscapeProperties.helpText, this.portraitProperties.helpText);
        this.titleText.visible = false;
        this.logo = TitanSprite.createSprite(this, "logo", this.landscapeProperties.logoProperties, this.portraitProperties?.logoProperties);
        this.helpButton = this.addChild(this.factory.createBonusHelp());
    }
    featurePresent() {
        this.seededPrizePicker.seed(this.model.getGameInstanceId());
        this.seededPaddlePicker.seed(this.model.getGameInstanceId());
        this.helpButton.setEnabled(!Service.isReplay());
        this.helpText.alpha = 0;
        this.visible = true;
        this.cat.removeCompleteHandlers();
        this.cat.play("idle", true, false, 0);
        this.siameseCrowdCat.play("idle", true, false, 0);
        this.allBoxes.forEach(box => box.alpha = 0);
        this.boxItems.forEach(b => b.parent?.removeChild(b));
        this.missedPrizeText0.parent?.removeChild(this.missedPrizeText0);
        this.missedPrizeText1.parent?.removeChild(this.missedPrizeText1);
        this.alpha = 1;
    }
    async featureStart() {
        const boxRippleDuration = 0.5;
        this.dispatchGame(GameLadyMeowSoundEvents.BOX1_IN);
        this.playBoxIntro("left");
        await delayFor(boxRippleDuration);
        this.dispatchGame(GameLadyMeowSoundEvents.BOX2_IN);
        this.playBoxIntro("middle");
        await delayFor(boxRippleDuration);
        this.dispatchGame(GameLadyMeowSoundEvents.BOX3_IN);
        this.playBoxIntro("right");
        await SpineComplete(this.boxRight);
        this.allBoxes.forEach(box => MouseUtil.setInteractive(box, true));
        this.availableCrowdIndices = this.paddles.map((_, index) => index);
        this.usedCrowdIndices = [];
        gsap.to(this.helpText, {
            alpha: 1,
            duration: 0.5,
            delay: 5,
        });
        this.dispatchGame(FeatureRedFishEvents.PICKS_READY);
    }
    playBoxIntro(direction) {
        const box = this.boxesByDirection[direction];
        box.alpha = 1;
        box.play(`entry_${direction}`);
        box.getSpine().update(1 / 60);
    }
    createParticleEmitter(landscapeProperties, portraitProperties) {
        const view = new AbstractGameView(this.factory, landscapeProperties, portraitProperties);
        this.addChild(view);
        const container = new LinkedListContainer();
        view.addChild(container);
        const particleEmitter = new ParticleEmitter(container, landscapeProperties);
        particleEmitter.stop();
        return { view, container, particleEmitter };
    }
    async revealPrizes(event) {
        this.allBoxes.forEach((box) => {
            MouseUtil.setInteractive(box, false);
        });
        this.helpButton.setEnabled(false);
        gsap.killTweensOf(this.helpText);
        this.helpText.alpha = 0;
        const payload = event.getPayload();
        const pickedIndex = payload["index"];
        const pickedInitialValue = payload["pickedInitialValue"];
        const missedInitialValues = payload["missedInitialValues"];
        this.missedPrizeText0.setText(this.formatCurrency(missedInitialValues[0]), true);
        this.missedPrizeText1.setText(this.formatCurrency(missedInitialValues[1]), true);
        const unpickedDirections = ALL_BOX_DIRECTIONS.filter((_, index) => index !== pickedIndex);
        this.pickedDirection = ALL_BOX_DIRECTIONS[pickedIndex];
        await this.removeUnpickedBoxes(unpickedDirections);
        await delayFor(0.5);
        await this.movePickedBoxToMiddle();
        await this.revealPickedPrize();
        await delayFor(0.25);
        await this.playCatBid(pickedInitialValue, 0, "initial");
        this.dispatchGame(FeatureRedFishEvents.REVEAL_PRIZES_COMPLETE);
    }
    async removeUnpickedBoxes(unpickedDirections) {
        addToTitanSpineSlot(this.boxesByDirection[unpickedDirections[0]], this.seededPrizePicker.pickFromArray(this.boxItems.filter(b => !b.parent)), "dynamic-item");
        addToTitanSpineSlot(this.boxesByDirection[unpickedDirections[1]], this.seededPrizePicker.pickFromArray(this.boxItems.filter(b => !b.parent)), "dynamic-item");
        addToTitanSpineSlot(this.boxesByDirection[unpickedDirections[0]], this.missedPrizeText0, "dynamic-item");
        addToTitanSpineSlot(this.boxesByDirection[unpickedDirections[1]], this.missedPrizeText1, "dynamic-item");
        this.dispatchGame(GameLadyMeowSoundEvents.BOX_OUT);
        await Promise.all(unpickedDirections.map((direction) => {
            const box = this.boxesByDirection[direction];
            box.play(`${direction}_poof`, false, false, 0);
            box.getSpine().update(1 / 60);
            return SpineComplete(box);
        }));
    }
    ;
    async movePickedBoxToMiddle() {
        const shouldMove = (this.pickedDirection !== "middle");
        const box = this.boxesByDirection[this.pickedDirection];
        if (shouldMove) {
            box.play(`${this.pickedDirection}_to_middle`, false, false, 0);
            box.getSpine().update(1 / 60);
            await SpineComplete(box);
            await delayFor(1);
        }
    }
    async revealPickedPrize() {
        this.dispatchGame(GameLadyMeowSoundEvents.BOX_REVEAL);
        const box = this.boxesByDirection[this.pickedDirection];
        addToTitanSpineSlot(box, this.seededPrizePicker.pickFromArray(this.boxItems.filter(b => !b.parent)), "dynamic-item");
        box.play(`auction_middle_open_item`, false, false, 0);
        await SpineComplete(box);
    }
    async bid(event) {
        const payload = event.getPayload();
        const kiss = payload["kiss"];
        this.prizeTexts.forEach(prizeText => {
            prizeText.setText("");
        });
        await this.playCatBid(kiss.cumulativeValue, kiss.value, kiss.type === RedFishKissType.gold ? "double" : "normal");
        this.dispatchGame(FeatureRedFishEvents.KISS_COMPLETE);
    }
    async playCatBid(bidValue, valueChange, changeType) {
        if (changeType === "initial") {
            this.bidChangeText.setText("");
            await this.doRegularCatBid(bidValue);
        }
        else if (changeType === "normal") {
            this.bidChangeText.setText(`+${this.formatCurrency(valueChange)}`, true);
            await this.doRegularCatBid(bidValue);
        }
        else {
            this.bidChangeText.setText(`x2`, true);
            await this.doSiameseCatBid(bidValue);
        }
    }
    async doRegularCatBid(value) {
        if (this.availableCrowdIndices.length === 0) {
            // reset the arrays. Make sure the last used index can't get picked again immediately
            const lastUsedCrowdIndex = this.usedCrowdIndices[this.usedCrowdIndices.length - 1];
            this.availableCrowdIndices = this.usedCrowdIndices.slice();
            this.availableCrowdIndices.splice(this.usedCrowdIndices.indexOf(lastUsedCrowdIndex), 1);
            this.usedCrowdIndices = [lastUsedCrowdIndex];
        }
        const index = this.availableCrowdIndices[Math.floor(this.seededPaddlePicker.random() * this.availableCrowdIndices.length)];
        this.usedCrowdIndices.push(index);
        this.availableCrowdIndices.splice(this.availableCrowdIndices.indexOf(index), 1);
        const paddle = this.paddles[index];
        const prizeText = this.prizeTexts[index];
        prizeText.setText(this.formatCurrency(value), true);
        const { direction, distance } = this.getLookDirectionAndDistance(paddle);
        this.cat.play(`landscape_look_${direction}_nogavel-${distance}`, false, false, 0);
        this.cat.getSpine().update(0.01);
        SpineComplete(this.cat).then(() => {
            const track = this.cat.getSpine().state.tracks[0];
            if (track && track.animation.name !== "idle" && track.trackTime < track.animation.duration) {
                return;
            }
            this.cat.play(`idle`, true, false, 0);
        });
        this.dispatchGame(GameLadyMeowSoundEvents.BID);
        this.dispatchGame(GameLadyMeowSoundEvents.RAISE_PADDLE);
        paddle.play(`paddle_up`, false, false, 0);
        await delayFor(0.5);
        this.dispatchGame(GameLadyMeowSoundEvents.PADDLE_DING);
        this.showBidChangeAnimation(paddle);
        await SpineComplete(paddle);
        paddle.play(`paddle_hold`, false, false, 0);
        await delayFor(1.5);
        this.dispatchGame(GameLadyMeowSoundEvents.LOWER_PADDLE);
        paddle.play(`paddle_down`, false, false, 0);
        await SpineComplete(paddle);
    }
    getLookDirectionAndDistance(paddle) {
        paddle.visible = false;
        paddle.play(`paddle_hold`, false, false, 0);
        paddle.getSpine().update(0.01);
        paddle.updateTransform();
        const valueSlot = getTitanSpineSlot(paddle, `dynamic-value`);
        const globalPaddleTextPosition = valueSlot.parent.toGlobal(valueSlot);
        const localPaddleTextPosition = this.toLocal(globalPaddleTextPosition);
        resetTitanSpine(paddle);
        paddle.getSpine().update(0.01);
        paddle.updateTransform();
        paddle.visible = true;
        return {
            direction: localPaddleTextPosition.x < this.cat.x ? "left" : "right",
            distance: Math.abs(localPaddleTextPosition.x - this.cat.x) > 200 ? "0" : "1"
        };
    }
    async doSiameseCatBid(value) {
        this.siamesePrizeText.setText(this.formatCurrency(value), true);
        this.cat.removeCompleteHandlers();
        this.cat.play([`landscape_look_siamese`, `portrait_look_siamese`], false, false, 0);
        this.cat.getSpine().update(0.01);
        this.dispatchGame(GameLadyMeowSoundEvents.BID);
        this.dispatchGame(GameLadyMeowSoundEvents.RAISE_PADDLE);
        this.siameseCrowdCat.play(`paddle_lift`, false, false, 0);
        await delayFor(0.5);
        this.dispatchGame(GameLadyMeowSoundEvents.SIAMESE_PADDLE_DING);
        this.showBidChangeAnimation(this.siameseCrowdCat);
        await SpineComplete(this.siameseCrowdCat);
        this.siameseCrowdCat.play(`paddle_hold`, false, false, 0);
        await delayFor(2);
        this.dispatchGame(GameLadyMeowSoundEvents.LOWER_PADDLE);
        this.siameseCrowdCat.play(`paddle_down`, false, false, 0);
        await SpineComplete(this.siameseCrowdCat);
        this.siameseCrowdCat.play(`idle`, true, false, 0);
    }
    showBidChangeAnimation(bidderCat) {
        const valueSlot = getTitanSpineSlot(bidderCat, `dynamic-value`);
        const globalPaddleTextPosition = valueSlot.parent.toGlobal(valueSlot);
        const localPaddleTextPosition = this.toLocal(globalPaddleTextPosition);
        this.bidChangeText.parent.addChild(this.bidChangeText);
        const bidChangeOffset = new Point(100, -100);
        const bidChangeRiseDistance = 100;
        this.bidChangeText.position.set(localPaddleTextPosition.x + bidChangeOffset.x, localPaddleTextPosition.y + bidChangeOffset.y);
        this.bidChangeText.alpha = 1;
        gsap
            .timeline()
            .to(this.bidChangeText, {
            pixi: {
                y: this.bidChangeText.y - bidChangeRiseDistance
            },
            duration: 2
        })
            .to(this.bidChangeText, {
            alpha: 0,
            duration: 0.5,
        }, "-=0.5")
            .play();
    }
    async sold() {
        this.cat.removeCompleteHandlers();
        gsap.delayedCall(0.3, () => this.dispatchGame(GameLadyMeowSoundEvents.GAVEL));
        this.cat.play("celebration", false, false, 0);
        await delayFor(3.33);
        this.cat.play("idle", true, false, 0);
        this.dispatchGame(FeatureRedFishEvents.SOLD_COMPLETE);
    }
    clear() {
        this.visible = false;
        this.alpha = 1;
        this.boxItems.forEach(item => item.parent?.removeChild(item));
    }
    handleRotate(event) {
        super.handleRotate(event);
        this.paddles.forEach((paddle) => updateResponsiveSpineSlotsChildren(paddle));
        updateResponsiveSpineSlotsChildren(this.siameseCrowdCat);
        this.allBoxes.forEach(box => updateResponsiveSpineSlotsChildren(box));
    }
}
FeatureRedFish.REPLAY_PICK_INDEX = 1;
export class FeatureRedFishEvents {
}
FeatureRedFishEvents.FEATURE_PRESENT = new EventObject("FeatureRedFishEvents.FEATURE_PRESENT");
FeatureRedFishEvents.PICKS_READY = new EventObject("FeatureRedFishEvents.PICKS_READY");
FeatureRedFishEvents.PICK_SELECTED = new EventObject("FeatureRedFishEvents.PICK_SELECTED");
FeatureRedFishEvents.REVEAL_PRIZES = new EventObject("FeatureRedFishEvents.REVEAL_PRIZES");
FeatureRedFishEvents.REVEAL_PRIZES_COMPLETE = new EventObject("FeatureRedFishEvents.REVEAL_PRIZES_COMPLETE");
FeatureRedFishEvents.KISS = new EventObject("FeatureRedFishEvents.KISS");
FeatureRedFishEvents.KISS_COMPLETE = new EventObject("FeatureRedFishEvents.KISS_COMPLETE");
FeatureRedFishEvents.SOLD = new EventObject("FeatureRedFishEvents.SOLD");
FeatureRedFishEvents.SOLD_COMPLETE = new EventObject("FeatureRedFishEvents.SOLD_COMPLETE");
FeatureRedFishEvents.DROP_BOWL_IN = new EventObject("FeatureRedFishEvents.DROP_BOWL_IN");
FeatureRedFishEvents.DROP_BOWL_IN_COMPLETE = new EventObject("FeatureRedFishEvents.DROP_BOWL_IN_COMPLETE");
FeatureRedFishEvents.FISH_ENTER_BOWL = new EventObject("FeatureRedFishEvents.FISH_ENTER_BOWL");
FeatureRedFishEvents.CLEAR = new EventObject("FeatureRedFishEvents.CLEAR");
FeatureRedFishEvents.FEATURE_START = new EventObject("FeatureRedFishEvents.FEATURE_START");
FeatureRedFishEvents.PARTICLE_SPARKLES = new EventObject("FeatureRedFishEvents.PARTICLE_SPARKLES");
FeatureRedFishEvents.PARTICLE_BUBBLE_EDGES = new EventObject("FeatureRedFishEvents.PARTICLE_BUBBLE_EDGES");
FeatureRedFishEvents.PARTICLE_BUBBLE_PRIZE = new EventObject("FeatureRedFishEvents.PARTICLE_BUBBLE_PRIZE");
//# sourceMappingURL=FeatureRedFish.js.map